import React from "react";
import { motion } from "framer-motion";
import { Rocket, Plus } from "lucide-react";
import { resolveImage } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const HERO_IMG = resolveImage("/api/static/images/hero-island2.png");
const HERO_FALLBACK_3 = resolveImage("/api/static/images/hero-island.png");
const HERO_FALLBACK_2 = resolveImage("/api/static/images/hero-innovation.png");
const HERO_FALLBACK = "https://images.pexels.com/photos/7972949/pexels-photo-7972949.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const RevealLine = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const HERO_BG = [
  "radial-gradient(ellipse 900px 500px at 78% 30%, rgba(60,90,220,0.35), transparent 60%)",
  "radial-gradient(ellipse 600px 400px at 95% 80%, rgba(80,60,200,0.25), transparent 60%)",
  "linear-gradient(135deg, #080b22 0%, #0d1240 45%, #141a52 100%)",
].join(", ");

const STARFIELD = [
  "radial-gradient(2px 2px at 20% 30%, rgba(255,255,255,0.5), transparent)",
  "radial-gradient(2px 2px at 60% 15%, rgba(255,255,255,0.4), transparent)",
  "radial-gradient(1.5px 1.5px at 85% 60%, rgba(255,255,255,0.5), transparent)",
  "radial-gradient(1.5px 1.5px at 40% 80%, rgba(255,255,255,0.3), transparent)",
  "radial-gradient(2px 2px at 92% 25%, rgba(255,255,255,0.5), transparent)",
  "radial-gradient(1.5px 1.5px at 10% 65%, rgba(255,255,255,0.35), transparent)",
].join(", ");

const TITLE_GRADIENT = "bg-gradient-to-r from-white via-[#a9c1ff] to-[#7c93ff] bg-clip-text text-transparent";

const NeuralField = ({ className = "", opacity = 0.35 }) => {
  const nodes = [[60, 90], [150, 40], [240, 110], [330, 55], [420, 120], [520, 70], [90, 220], [200, 260], [310, 210], [430, 250], [540, 200], [140, 380], [280, 350], [410, 390], [530, 330]];
  const edges = [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [0, 6], [2, 7], [6, 7], [3, 8], [7, 8], [4, 9], [8, 9], [5, 10], [9, 10], [6, 11], [7, 12], [11, 12], [8, 13], [12, 13], [9, 14], [13, 14]];
  return (
    <svg className={className} style={{ opacity }} viewBox="0 0 600 450" fill="none" aria-hidden="true">
      {edges.map(([a, b], i) => (
        <line key={i} x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]} stroke="rgba(125,164,255,0.28)" strokeWidth="1" />
      ))}
      {nodes.map(([x, y], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r={i % 4 === 0 ? 4 : 2.5} fill="rgba(125,164,255,0.55)" />
          {i % 4 === 0 && <circle cx={x} cy={y} r="8" stroke="rgba(125,164,255,0.3)" strokeWidth="1" />}
        </g>
      ))}
    </svg>
  );
};

export default function KineticHero({ onSubmit }) {
  const { t, lang } = useLang();
  const scrollToMarket = () => document.getElementById("marketplace")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden"
      style={{ background: HERO_BG }}
    >
      <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: STARFIELD }} />
      <NeuralField className="pointer-events-none absolute -left-10 top-10 z-0 hidden h-[420px] w-[560px] lg:block" opacity={0.18} />

      <div className="relative z-10 mx-auto grid max-w-[1560px] grid-cols-1 items-center gap-5 px-5 pb-12 pt-36 sm:px-8 lg:grid-cols-[40%_60%] lg:pb-10 lg:pt-28 lg:pl-8 lg:pr-8">
        <div className="text-center lg:text-left">
          <h1
            className="font-display text-[clamp(30px,3.4vw,52px)] font-extrabold leading-[1.15] tracking-[-0.5px]"
            data-testid="hero-title"
          >
            <RevealLine delay={0.5}>
              <span className={TITLE_GRADIENT}>{t.hero_brand1}</span>
            </RevealLine>
            <RevealLine delay={0.68}>
              <span className={TITLE_GRADIENT}>{t.hero_brand2}</span>
            </RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-5 text-[clamp(15px,1.3vw,19px)] font-semibold text-[#eef1ff]"
            data-testid="hero-subtitle"
          >
            {lang === "ko" ? t.hero_tag_ko : t.hero_tag}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-8 flex flex-col items-stretch justify-center gap-3.5 sm:flex-row lg:justify-start"
          >
            <button
              onClick={scrollToMarket}
              data-testid="hero-explore-button"
              className="group flex items-center justify-center gap-2 rounded-[9px] bg-[#4361f0] px-[clamp(14px,1.6vw,22px)] py-[clamp(8px,0.9vw,12px)] text-[clamp(11px,0.95vw,13.5px)] font-bold text-white shadow-[0_8px_20px_rgba(67,97,240,0.35)] transition-all duration-300 hover:bg-[#5271ff] hover:shadow-[0_12px_32px_rgba(67,97,240,0.5)]"
            >
              <Rocket size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5" />
              {t.hero_cta1}
            </button>
            <button
              onClick={onSubmit}
              data-testid="hero-submit-button"
              className="flex items-center justify-center gap-2 rounded-[9px] border-[1.5px] border-white/35 bg-transparent px-[clamp(14px,1.6vw,22px)] py-[clamp(8px,0.9vw,12px)] text-[clamp(11px,0.95vw,13.5px)] font-bold text-white transition-all duration-300 hover:border-white/70 hover:bg-white/5"
            >
              <Plus size={16} /> {t.hero_cta2}
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden h-[305px] items-center justify-center lg:flex xl:h-[350px]"
        >
          <NeuralField className="absolute -inset-10 z-0 h-[calc(100%+80px)] w-[calc(100%+80px)]" opacity={0.4} />
          <img
            src={HERO_IMG}
            onError={(e) => {
              if (!e.currentTarget.dataset.fb3) { e.currentTarget.dataset.fb3 = "1"; e.currentTarget.src = HERO_FALLBACK_3; }
              else if (!e.currentTarget.dataset.fb2) { e.currentTarget.dataset.fb2 = "1"; e.currentTarget.src = HERO_FALLBACK_2; }
              else { e.currentTarget.src = HERO_FALLBACK; }
            }}
            alt="Keimyung campus building surrounded by AI service category icons: Research, Education, AI Agent, Administration, Healthcare, Content"
            className="relative z-10 h-full w-full object-contain drop-shadow-[0_20px_40px_rgba(0,0,20,0.5)]"
            data-testid="hero-image"
          />
        </motion.div>
      </div>
    </section>
  );
}
